import { ExportContextProvider } from '@jgp-er-dev/charts'
import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'

import { getMenuList } from './clients/compass/menu'
import AIAssistantButton from './components/AIAssistantButton'
import LoadingContainer from './components/LoadingContainer'
import OpaqueBackdrop from './components/OpaqueBackdrop'
import SafeBoundary from './components/SafeBoundary'
import SideBar from './components/SideBar'
import { SideBarTreeLink, SideBarTreeSection } from './components/SideBar'
import Spinner from './components/Spinner'
import TopBar from './components/TopBar'
import { ModalContextProvider } from './contexts/ModalContext'
import { NotificationsContextProvider } from './contexts/NotificationsContext'
import { ToastContextProvider } from './contexts/ToastContext'
import { UserContextProvider, useUser } from './contexts/UserContext'
import { Path } from './utils/constants'
import { sideBarTree as staticSideBarTree } from './utils/constants'
import { PrioritizablePromise } from './utils/promise-queue'
import version from './version.json'

const AnalystNotesPage = lazy(() => import('./pages/analyst-notes/pages'))
const AnnotationPage = lazy(() => import('./pages/annotation/pages'))
const CurvaEmpresasV2Page = lazy(
  () => import('./pages/curva-empresas-v2/pages')
)
const PipelineAnaliseCreditoPage = lazy(
  () => import('./pages/pipeline-analise-credito/pages')
)
const DadosDeMercadoPage = lazy(() => import('./pages/dados-de-mercado/pages'))
const HdsPage = lazy(() => import('./pages/hds/pages'))
const CallsTradesPage = lazy(() => import('./pages/calls-trades/pages'))
const PeersMercadoPage = lazy(() => import('./pages/peers-mercado/pages'))
const BasketsPage = lazy(() => import('./pages/baskets/pages'))
const ESGPage = lazy(() => import('./pages/esg/pages'))
const IntradayMonitorPage = lazy(() => import('./pages/intraday-monitor/pages'))
const LaunchpadPage = lazy(() => import('./pages/launchpad/pages'))
const LoginPage = lazy(() => import('./pages/login'))
const MomentumScreeningPage = lazy(
  () => import('./pages/momentum-screening/pages')
)
const NotFoundPage = lazy(() => import('./pages/not-found'))
const PortfolioPage = lazy(() => import('./pages/portfolio/pages'))
const PresentationsPage = lazy(() => import('./pages/presentations/pages'))
const RegisterPage = lazy(() => import('./pages/register/pages'))
const ReportManagerPage = lazy(() => import('./pages/report-manager/pages'))
const ReportsPage = lazy(() => import('./pages/reports/pages'))
const ResetPasswordPage = lazy(() => import('./pages/resetpassword'))
const SearchPage = lazy(() => import('./pages/search/pages'))
const SharedChartPage = lazy(() => import('./pages/sharedchart/pages'))
const TestPage = lazy(() => import('./pages/test/pages'))
const UpdateSearchPage = lazy(() => import('./pages/update-search'))
const UserPage = lazy(() => import('./pages/user/pages'))
const ValuationPage = lazy(() => import('./pages/valuation/pages'))

type DeckNode = SideBarTreeSection | SideBarTreeLink

// Build the Semicon menu subtree from the equity-decks manifest
// (/decks/index.json). The manifest is a (possibly nested) array of nodes:
//   leaf  → { id, title, entry }        → a clickable deck link
//   group → { title, children: [...] }  → a drill-down submenu (folder on R:)
// Children are pre-sorted alphabetically by the manifest generator. Anything
// malformed is skipped so a bad manifest can never crash the sidebar (its
// search lowercases every title — a non-string title would take it down). A
// group with no valid children is dropped. Also tolerant of the legacy flat
// manifest (an array of leaves) so old/new frontend & manifest interoperate.
const buildDeckNodes = (raw: unknown): DeckNode[] => {
  if (!Array.isArray(raw)) {
    return []
  }
  const nodes: DeckNode[] = []
  for (const item of raw as any[]) {
    const title = item?.title
    if (typeof title !== 'string' || !title) continue
    if (Array.isArray(item?.children)) {
      const children = buildDeckNodes(item.children)
      if (children.length > 0) {
        nodes.push({ title, children })
      }
      continue
    }
    const id = item?.id
    if (typeof id !== 'string' || !id) continue
    const rawEntry = item?.entry
    const entry =
      typeof rawEntry === 'string' && rawEntry && rawEntry !== 'index.html'
        ? rawEntry
        : ''
    nodes.push({
      title,
      path: `${Path.REPORTS_SEMICON_DECK}/${id}/${entry}`,
    })
  }
  return nodes
}

// Append the auto-listed deck nodes (leaves and/or groups) to the Semicon node,
// wherever it sits in the tree (Menu principal → Equity → Semicon). Returns a
// new tree.
const withSemiconDecks = (
  node: SideBarTreeSection,
  decks: DeckNode[]
): SideBarTreeSection => ({
  ...node,
  children: node.children.map((child) => {
    if (!('children' in child)) {
      return child
    }
    if (child.title === 'Semicon') {
      return { ...child, children: [...child.children, ...decks] }
    }
    return withSemiconDecks(child, decks)
  }),
})

const useSideBarTree = () => {
  const { email } = useUser()

  const [v1MenuList, setV1MenuList] = useState<SideBarTreeSection[]>([])
  const [deckNodes, setDeckNodes] = useState<DeckNode[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const menuListPromiseRef = useRef<PrioritizablePromise<any> | null>(null)

  useEffect(() => {
    if (!email) {
      return
    }

    setLoading(true)
    menuListPromiseRef.current = getMenuList(email)
      .thenP((res) => setV1MenuList(res))
      .catchP((err) => console.error('Error fetching menu list: ', err))
      .finallyP(() => setLoading(false))
  }, [email])

  // Auto-list Semicon decks from the equity-decks manifest (/decks/index.json).
  // Best-effort: on any failure the Semicon menu just shows its static entries.
  useEffect(() => {
    if (!email) {
      return
    }
    let cancelled = false
    fetch('/decks/index.json', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((manifest: unknown) => {
        if (cancelled) {
          return
        }
        // buildDeckNodes validates/skips anything malformed, so the sidebar
        // (shared by every team) can never be crashed by a bad manifest.
        setDeckNodes(buildDeckNodes(manifest))
      })
      .catch(() => {
        if (!cancelled) {
          setDeckNodes([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [email])

  const sideBarTree = useMemo(() => {
    let base = staticSideBarTree
    if (deckNodes.length > 0) {
      try {
        base = withSemiconDecks(staticSideBarTree, deckNodes)
      } catch (err) {
        // Never let deck injection break the menu — fall back to the static tree.
        console.error('Failed to inject Semicon decks into the menu:', err)
        base = staticSideBarTree
      }
    }
    return {
      title: base.title,
      children: [
        ...base.children,
        ...(v1MenuList.length > 0 ? v1MenuList : []),
      ],
    }
  }, [v1MenuList, deckNodes])

  return {
    loading,
    sideBarTree,
  }
}

const RedirectToLogin: React.FC = () => {
  const { pathname, search } = useLocation()

  return (
    <Redirect
      to={{
        pathname: Path.LOGIN,
        search: `?redirect=${encodeURIComponent(pathname + search)}`,
      }}
    />
  )
}

// Contraparte do RedirectToLogin para quem JÁ está logado: links externos
// (e-mail de Analyst Notes) apontam para /login?redirect=<destino>, porque o
// servidor responde 302 /login seco em qualquer outra rota sem sessão — só
// /login chega ao React com a query intacta. Deslogado, o LoginPage honra o
// ?redirect após autenticar; logado, esta rota entrega o destino direto (sem
// ela, /login cairia no catch-all NotFound e o destino morreria).
const RedirectFromLogin: React.FC = () => {
  const { search } = useLocation()
  // URLSearchParams.get já devolve o valor percent-decodificado.
  const redirect = new URLSearchParams(search).get('redirect')

  return <Redirect to={redirect || Path.HOME} />
}

const LoadingFallback = () => (
  <OpaqueBackdrop position="fixed" color="white">
    <LoadingContainer loading spinnerSize={48} />
  </OpaqueBackdrop>
)

const App: React.FC = () => {
  const [sideBarOpen, setSideBarOpen] = useState<boolean>(false)
  const { checkPermissions, email, loading, isWhitelabelUser, theme } =
    useUser()
  const { loading: sideBarLoading, sideBarTree } = useSideBarTree()
  const hideTopBar = !!useRouteMatch(Path.INTRADAY_MONITOR)

  useEffect(() => {
    ;(window as any).jgp = { version: version.version }
  })

  return loading || sideBarLoading ? (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-100">
      <Spinner className="text-success-dark" size={64} />
    </div>
  ) : !email ? (
    <Switch>
      <Route path={Path.LOGIN} exact>
        <Suspense fallback={<LoadingFallback />}>
          <LoginPage />
        </Suspense>
      </Route>
      <Route path={Path.REGISTER}>
        <Suspense fallback={<LoadingFallback />}>
          <RegisterPage />
        </Suspense>
      </Route>
      <Route path={Path.RESET_PASSWORD}>
        <Suspense fallback={<LoadingFallback />}>
          <ResetPasswordPage />
        </Suspense>
      </Route>
      <Route>
        <RedirectToLogin />
      </Route>
    </Switch>
  ) : (
    <ExportContextProvider className="z-50" theme={theme!}>
      <div className="relative flex h-screen min-h-full w-full min-w-min flex-col bg-gray-100 font-work-sans">
        <SafeBoundary>
          <SideBar
            open={sideBarOpen}
            tree={sideBarTree}
            checkPermissions={checkPermissions}
            display={isWhitelabelUser ? 'whitelabel' : 'default'}
            onClose={() => setSideBarOpen(false)}
          />
        </SafeBoundary>
        {/* O Intraday Monitor usa a tela inteira; o menu abre pela própria página. */}
        {!hideTopBar && (
          <TopBar
            className="flex-shrink-0"
            onMenuOpen={() => setSideBarOpen(true)}
          />
        )}
        <Switch>
          <Route path={Path.LOGIN} exact>
            <RedirectFromLogin />
          </Route>
          <Route path={Path.ANALYST_NOTES}>
            <Suspense fallback={<LoadingFallback />}>
              <AnalystNotesPage />
            </Suspense>
          </Route>
          <Route path={Path.ANNOTATION}>
            <Suspense fallback={<LoadingFallback />}>
              <AnnotationPage />
            </Suspense>
          </Route>
          <Route path={Path.CURVA_EMPRESAS_V2}>
            <Suspense fallback={<LoadingFallback />}>
              <CurvaEmpresasV2Page />
            </Suspense>
          </Route>
          {/* '/research' é o path atual; mantém '/pipeline-analise-credito'
              (legado) pra não quebrar bookmarks e deep-links de e-mail já
              enviados (o ?tab=notas&nota= é preservado — mesma rota). */}
          <Route
            path={[Path.PIPELINE_ANALISE_CREDITO, '/pipeline-analise-credito']}
          >
            <Suspense fallback={<LoadingFallback />}>
              <PipelineAnaliseCreditoPage />
            </Suspense>
          </Route>
          {/* O Controle de Hedge virou a sub-aba "Hedge" da Análise da Carteira
              (ver CarteiraPageTabs). A rota própria fica como REDIRECT em vez de
              ser apagada: é o link que o usuário tem guardado. Assim a tela
              existe em UM lugar só, sempre com as abas.

              O item "Controle Hedge" do menu lateral foi RETIRADO em 28/09/2026
              (a pedido): apontava para este redirect e era uma segunda porta
              para uma aba que já se alcança por Análise da Carteira. O redirect
              continua — some do menu, não dos favoritos de quem já o salvou. */}
          <Route path={Path.CONTROLE_HEDGE}>
            <Redirect
              to={`${Path.REPORTS_CREDIT_LIQUID_PORTFOLIO_IMPACT}?tab=hedge`}
            />
          </Route>
          <Route path={Path.DADOS_DE_MERCADO}>
            <Suspense fallback={<LoadingFallback />}>
              <DadosDeMercadoPage />
            </Suspense>
          </Route>
          <Route path={Path.HDS}>
            <Suspense fallback={<LoadingFallback />}>
              <HdsPage />
            </Suspense>
          </Route>
          <Route path={Path.CALLS_TRADES}>
            <Suspense fallback={<LoadingFallback />}>
              <CallsTradesPage />
            </Suspense>
          </Route>
          <Route path={Path.PEERS_MERCADO}>
            <Suspense fallback={<LoadingFallback />}>
              <PeersMercadoPage />
            </Suspense>
          </Route>
          <Route path={Path.BASKETS}>
            <Suspense fallback={<LoadingFallback />}>
              <BasketsPage />
            </Suspense>
          </Route>
          <Route path={Path.ESG}>
            <Suspense fallback={<LoadingFallback />}>
              <ESGPage />
            </Suspense>
          </Route>
          <Route path={Path.HOME} exact>
            <Redirect to={Path.ESG_FRAMEWORK_MANAGER} />
          </Route>
          <Route path={Path.INTRADAY_MONITOR}>
            <Suspense fallback={<LoadingFallback />}>
              <IntradayMonitorPage onMenuOpen={() => setSideBarOpen(true)} />
            </Suspense>
          </Route>
          <Route path={Path.LAUNCHPAD}>
            <Suspense fallback={<LoadingFallback />}>
              <LaunchpadPage />
            </Suspense>
          </Route>
          <Route path={Path.LOGIN} exact>
            <Redirect to={Path.HOME} />
          </Route>
          <Route path={Path.MOMENTUM_SCREENING}>
            <Suspense fallback={<LoadingFallback />}>
              <MomentumScreeningPage />
            </Suspense>
          </Route>
          <Route path={Path.PORTFOLIO}>
            <Suspense fallback={<LoadingFallback />}>
              <PortfolioPage />
            </Suspense>
          </Route>
          <Route path={Path.PRESENTATIONS}>
            <Suspense fallback={<LoadingFallback />}>
              <PresentationsPage />
            </Suspense>
          </Route>
          <Route path={Path.REGISTER}>
            <Suspense fallback={<LoadingFallback />}>
              <RegisterPage />
            </Suspense>
          </Route>
          <Route path={Path.REPORTS}>
            <Suspense fallback={<LoadingFallback />}>
              <ReportsPage />
            </Suspense>
          </Route>
          <Route path={Path.REPORT_MANAGER}>
            <Suspense fallback={<LoadingFallback />}>
              <ReportManagerPage />
            </Suspense>
          </Route>
          <Route path={Path.RESET_PASSWORD}>
            <Suspense fallback={<LoadingFallback />}>
              <ResetPasswordPage />
            </Suspense>
          </Route>
          <Route path={Path.SEARCH}>
            <Suspense fallback={<LoadingFallback />}>
              <SearchPage />
            </Suspense>
          </Route>
          <Route path={Path.SHARED_CHART}>
            <Suspense fallback={<LoadingFallback />}>
              <SharedChartPage />
            </Suspense>
          </Route>
          <Route path={Path.TEST}>
            <Suspense fallback={<LoadingFallback />}>
              <TestPage />
            </Suspense>
          </Route>
          <Route path={Path.UPDATE_SEARCH}>
            <Suspense fallback={<LoadingFallback />}>
              <UpdateSearchPage />
            </Suspense>
          </Route>
          <Route path={Path.USER}>
            <Suspense fallback={<LoadingFallback />}>
              <UserPage />
            </Suspense>
          </Route>
          <Route path={Path.VALUATION}>
            <Suspense fallback={<LoadingFallback />}>
              <ValuationPage />
            </Suspense>
          </Route>
          <Route>
            <Suspense fallback={<LoadingFallback />}>
              <NotFoundPage />
            </Suspense>
          </Route>
        </Switch>

        {/* AI Assistant floating button - appears on all pages */}
        <AIAssistantButton />
      </div>
    </ExportContextProvider>
  )
}

const EnhancedApp: React.FC = () => (
  <Router>
    <UserContextProvider>
      <ToastContextProvider>
        <ModalContextProvider>
          <NotificationsContextProvider>
            <App />
          </NotificationsContextProvider>
        </ModalContextProvider>
      </ToastContextProvider>
    </UserContextProvider>
  </Router>
)

export default EnhancedApp
